import { useEffect, useState } from 'react';

// this hook dynamically adds a script tag to the page.
function useScript(src) {
  // Keep track of script status ("idle", "loading", "ready", "error")
  const [status, setStatus] = useState(src ? 'loading' : 'idle');
  useEffect(() => {
      // Allow falsy src value if waiting on other data needed for
      // constructing the script URL passed to this hook.
    if (!src) {
      setStatus('idle');
      return;
    }

    // Remove all Qrvey scripts before adding new one
    document.querySelectorAll('script[src*="qrvey-an-widgets"]').forEach(s => s.remove());
    // Fetch existing script element by src
    // It may have been added by another intance of this hook
    // Check if script already exists
    let script: HTMLScriptElement = document.querySelector(`script[src="${src}"]`);
    let created = false;
    if (!script) {
      // Create script
      script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.setAttribute('data-status', 'loading');
      // Add script to document body
      document.body.appendChild(script);
      created = true;
    } else {
      setStatus(script.getAttribute('data-status') || 'loading');
    }
    // Store status in attribute on script
    // This can be read by other instances of this hook
    const setAttributeFromEvent = (event) => {
      script.setAttribute(
        'data-status',
        event.type === 'load' ? 'ready' : 'error',
      );
    };
    script.addEventListener('load', setAttributeFromEvent);
    script.addEventListener('error', setAttributeFromEvent);
    // Script event handler to update status in state
    // Note: Even if the script already exists we still need to add
    // event handlers to update the state for *this* hook instance.
    const setStateFromEvent = (event) => {
      setStatus(event.type === 'load' ? 'ready' : 'error');
    };
    // Add event listeners
    script.addEventListener('load', setStateFromEvent);
    script.addEventListener('error', setStateFromEvent);
    // Remove event listeners on cleanup
    return () => {
      script.removeEventListener('load', setAttributeFromEvent);
      script.removeEventListener('error', setAttributeFromEvent);
      script.removeEventListener('load', setStateFromEvent);
      script.removeEventListener('error', setStateFromEvent);
      if (created && script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  },[src]); // Only re-run effect if script src changes
  return status;
}

export default useScript;
